/**
 * Users — Roles tab (W1a-07)
 *
 * Renders the permission catalogue the SERVER serves
 * (`GET /users/permissions/catalogue`). Two role kinds:
 *
 *   - SHIPPED ROLES — read-only rows of the `roles` table, installed by
 *     c24/c25 and upserted by `db/seeds/roles.seed.ts`. Cannot be edited or
 *     deleted.
 *   - CUSTOM ROLES — per-tenant, user-managed via this UI. Create / edit /
 *     delete + assignment-count gate.
 *
 * The privilege-escalation guard at the API enforces that a creator cannot
 * grant a permission they don't currently have, and refuses outright the keys
 * no tenant role may carry (`UNGRANTABLE_PERMISSION`). The picker disables
 * those cells so the screen offers only what the platform can honour.
 *
 * CUSTOM ROLES GRANT REAL AUTHORITY (review BF-1). This header used to say
 * "F-W1a-07.1 (deferred): the runtime resolver does NOT yet consult custom
 * roles — assignments are decorative until that wires in". Since B1-T2
 * `resolvePrincipal` unions a custom role's stored grants into the principal
 * and `requirePermission` → `can()` enforces them on every request.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useEffect, Fragment } from 'react';
import {
  Plus, Shield, Lock, Users as UsersIcon, Check,
  MoreHorizontal, Pencil, Trash2, Copy, Eye, ChevronRight,
  Info, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  useRoles,
  useCreateRole,
  useUpdateRole,
  useDeleteRole,
  useCloneRole,
  usePermissionCatalogue,
  groupPermissionCatalogue,
  type RoleRow,
  type PermissionCatalogueRow,
} from '@/lib/use-users';
import { useHasPermission } from '@/lib/use-has-permission';

export const Route = createFileRoute('/users/roles')({
  component: RolesTab,
});

// ─── The catalogue is SERVED, not mirrored (Foundations 1 / B1-T3) ──────────
//
// Four constants stood here: `ALL_ACTIONS` (13 of the platform's 21),
// `ALL_RESOURCES` (12 of its 52), `RESOURCE_GROUPS` over those twelve, and
// `HIGH_RISK_ACTIONS`. The header called them "hand-mirrored here so the UI
// doesn't pull a server dependency", and the mirror was 40 resources short.
//
// So the permission picker drew a 13x12 grid of which most cells were not
// permission keys at all, and `write:tools`, `write:skills`, `write:prompts`,
// `write:workflows`, `write:library`, `read:projects`, `share:argus_live` and
// the rest had no row: an admin could not grant them, and a role that already
// held one rendered as holding nothing. That is the ledger's invisible grant.
//
// `GET /users/permissions/catalogue` serves every key with its label, group
// and risk flag, already ordered by group → resource → action. The picker and
// the role-detail matrix render what they are given and compute nothing:
// the columns are the actions the payload actually uses, the sections are its
// groups, and a cell with no key gets no checkbox.

function RolesTab() {
  const { roles, isLoading, error, refetch } = useRoles();
  const canWrite = useHasPermission('write', 'roles');
  const canDelete = useHasPermission('delete', 'roles');

  const [search, setSearch] = useState('');
  const [expandedRoleId, setExpandedRoleId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleRow | null>(null);
  const [deleteDialogRole, setDeleteDialogRole] = useState<RoleRow | null>(null);

  const filtered = useMemo(() => {
    if (!search) return roles;
    const q = search.toLowerCase();
    return roles.filter(r =>
      r.name.toLowerCase().includes(q) ||
      (r.description ?? '').toLowerCase().includes(q),
    );
  }, [roles, search]);

  const customCount = roles.filter(r => r.kind === 'custom').length;
  const cloneRole = useCloneRole();

  const handleClone = async (role: RoleRow) => {
    try {
      const created = await cloneRole.mutateAsync({ roleId: role.id });
      toast.success(`Cloned "${role.name}" as "${created.name}"`, {
        description: 'The copy is yours to edit.',
      });
    } catch (err) {
      const error = err as Error & { response?: { error?: { message?: string } } };
      toast.error(
        error.response?.error?.message ?? error.message ?? 'Could not clone role',
      );
    }
  };

  const openCreate = () => {
    setEditingRole(null);
    setEditorOpen(true);
  };

  const openEdit = (role: RoleRow) => {
    setEditingRole(role);
    setEditorOpen(true);
  };

  return (
    <div className="flex flex-col h-full p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Roles</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Permission templates. {roles.length} total ({customCount} custom). Shipped
            roles are read-only — clone one to make a version this tenant owns.
          </p>
        </div>
        {canWrite && (
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> New Role
          </Button>
        )}
      </div>

      {/*
        B1-T3 (review BF-1), kept deleted by B1-T4. The banner here used to
        tell an admin that a custom role was audited but granted nothing at
        runtime, and a follow-up wave would wire the resolver. That stopped
        being true at B1-T2: `resolvePrincipal` unions a custom role's stored
        grants into the principal and `requirePermission` → `can()` enforces
        them on every request. A screen that calls a grant decorative while the
        gateway honours it is the disagreement this rail exists to end — and a
        false statement in the product is worse than no statement, so the
        sentence is gone from the page AND from this comment, where a grep for
        it would otherwise still find it.
      */}
      {customCount > 0 && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription className="text-xs">
            Custom roles grant real runtime authority. Every permission ticked here is
            enforced by the API on each request for anyone holding the role, directly or
            through a group.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-3">
          <Input
            placeholder="Search roles…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-xs max-w-md"
          />
        </CardContent>
      </Card>

      {isLoading && (
        <Card><CardContent className="py-8 text-center text-xs text-muted-foreground">Loading roles…</CardContent></Card>
      )}
      {error != null && (
        <Card><CardContent className="py-8 text-center text-xs text-red-600">
          Could not load roles. <Button variant="link" size="sm" onClick={() => refetch()}>Retry</Button>
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {!isLoading && error == null && filtered.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center">
              <Shield className="h-8 w-8 mx-auto text-muted-foreground/30 mb-3" />
              {/* Closes #133 — explicit filter-empty / data-empty
                  distinction so the no-match search path doesn't read
                  as "tenant has no roles". */}
              {search.trim() !== '' ? (
                <>
                  <p className="text-sm font-medium">No roles match your search</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Try a different term or clear the search.
                  </p>
                  <Button size="sm" variant="outline" className="mt-3" onClick={() => setSearch('')}>
                    Clear search
                  </Button>
                </>
              ) : (
                <p className="text-sm font-medium">No roles defined yet</p>
              )}
            </CardContent>
          </Card>
        )}
        {filtered.map(role => (
          <RoleCard
            key={role.id}
            role={role}
            expanded={expandedRoleId === role.id}
            onToggle={() => setExpandedRoleId(expandedRoleId === role.id ? null : role.id)}
            canWrite={canWrite}
            canDelete={canDelete}
            onEdit={() => openEdit(role)}
            onDelete={() => setDeleteDialogRole(role)}
            onClone={() => handleClone(role)}
            cloning={cloneRole.isPending}
          />
        ))}
      </div>

      <RoleEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        role={editingRole}
      />

      <DeleteRoleDialog
        role={deleteDialogRole}
        onClose={() => setDeleteDialogRole(null)}
      />
    </div>
  );
}

/**
 * The reason a control is unavailable, said in the words the API would use
 * (founder decision C5). A disabled menu item with no explanation is how an
 * admin ends up filing "the Edit button does nothing".
 */
const C5_REASON =
  'Shipped roles are read-only — they stay as the platform ships them. Clone this role to edit your own copy.';

function RoleCard({
  role, expanded, onToggle, canWrite, canDelete, onEdit, onDelete, onClone, cloning,
}: {
  role: RoleRow;
  expanded: boolean;
  onToggle: () => void;
  canWrite: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onClone: () => void;
  cloning: boolean;
}) {
  const isPlatform = role.kind === 'platform';
  const isRetired = role.retiredAt !== null;
  // The count the SERVER computed. Counting `role.permissions.length` here is
  // the second answer B1b deleted (see `policy/effective-permissions.ts`).
  const totalPermissions = role.permissionCount;
  const editDisabled = isPlatform || isRetired || !canWrite;
  const editReason = isPlatform
    ? C5_REASON
    : isRetired
      ? 'This role is retired: it grants nothing and cannot be edited.'
      : !canWrite
        ? 'You do not have permission to edit roles.'
        : undefined;
  // A LIVE tenant role is always removable: the server RETIRES one that has
  // ever been assigned instead of deleting it, so there is no assignment count
  // that can block the control (and no 409 the screen cannot explain).
  //
  // AN ALREADY-RETIRED ROLE IS NOT (review B1-T4-R2 / B1T4-04). DELETE on one
  // answers `{ deleted: false, retired: true }` and removes nothing — the row
  // is a tombstone, kept so its assignment history stays readable
  // (upgrade-model.md §3) — so an enabled Delete promised a permanent removal
  // the server would never perform. The control says why instead.
  const deleteDisabled = isPlatform || isRetired || !canDelete;
  const deleteReason = isPlatform
    ? C5_REASON
    : isRetired
      ? 'This role is already retired. A retired role is kept so its assignment history stays readable; it cannot be removed.'
      : !canDelete
        ? 'You do not have permission to delete roles.'
        : undefined;

  return (
    <Card>
      <Collapsible open={expanded} onOpenChange={onToggle}>
        <div className="flex items-center gap-3 p-4">
          <CollapsibleTrigger asChild>
            <button className="flex items-center gap-3 flex-1 text-left">
              <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                <Shield className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold">{role.name}</span>
                  {isPlatform ? (
                    <Badge
                      variant="outline"
                      className="text-[9px] px-1.5 py-0 h-4 gap-1"
                      title={C5_REASON}
                    >
                      <Lock className="h-2 w-2" /> Platform
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4">
                      Custom
                    </Badge>
                  )}
                  {role.basedOnKey && (
                    <Badge
                      variant="outline"
                      className="text-[9px] px-1.5 py-0 h-4"
                      title={`Cloned from the ${role.basedOnKey} role`}
                    >
                      from {role.basedOnKey}
                    </Badge>
                  )}
                  {isRetired && (
                    <Badge
                      variant="outline"
                      className="text-[9px] px-1.5 py-0 h-4 text-amber-700 dark:text-amber-400"
                      title="Retired: this role grants nothing. Its assignment history is kept."
                    >
                      Retired
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 tabular-nums">
                    {totalPermissions} permissions
                  </Badge>
                  <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 gap-1">
                    <UsersIcon className="h-2 w-2" /> {role.assignedUserCount} users
                  </Badge>
                </div>
                {role.description && (
                  <p className="text-xs text-muted-foreground mt-0.5">{role.description}</p>
                )}
              </div>
              <ChevronRight className={cn(
                'h-4 w-4 text-muted-foreground transition-transform shrink-0',
                expanded && 'rotate-90',
              )} />
            </button>
          </CollapsibleTrigger>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Role actions">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-xs"
                onSelect={() => toast.info(`${role.assignedUserCount} user(s) have this role`)}
              >
                <Eye className="h-3.5 w-3.5 mr-2" /> View users
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-xs"
                disabled={editDisabled}
                onSelect={onEdit}
                title={editReason}
              >
                <Pencil className="h-3.5 w-3.5 mr-2" /> Edit role
              </DropdownMenuItem>
              {/*
                CLONE IS THE WAY A TENANT CHANGES A SHIPPED ROLE (C5). It is
                offered on every row, because cloning a tenant role to try a
                variant is the same act.
              */}
              <DropdownMenuItem
                className="text-xs"
                disabled={!canWrite || cloning || isRetired}
                onSelect={onClone}
                title={
                  isRetired
                    ? 'A retired role grants nothing, so there is nothing to clone.'
                    : !canWrite
                      ? 'You do not have permission to create roles.'
                      : 'Create a copy of this role that your tenant owns'
                }
              >
                <Copy className="h-3.5 w-3.5 mr-2" />
                {cloning ? 'Cloning…' : 'Clone role'}
              </DropdownMenuItem>
              <DropdownMenuItem className="text-xs" onSelect={() => {
                navigator.clipboard.writeText(role.key);
                toast.success('Role key copied');
              }}>
                <Copy className="h-3.5 w-3.5 mr-2" /> Copy role key
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-xs text-red-600 dark:text-red-400"
                disabled={deleteDisabled}
                onSelect={onDelete}
                title={deleteReason}
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                {/*
                  The verb the SERVER will use. It chooses from "has this role
                  ever been assigned" — every row on file, a deleted user's
                  included — not from the live holder count, which is what this
                  label read and why it offered "Delete role" for a role the
                  server then retired (review B1-T4-R2 / B1T4-04).
                */}
                {isRetired
                  ? 'Retired'
                  : role.everAssigned
                    ? 'Retire role'
                    : 'Delete role'}
                {role.assignedUserCount > 0 && (
                  <span className="ml-1 text-[9px]">({role.assignedUserCount} holders)</span>
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <CollapsibleContent>
          <div className="border-t px-4 py-4 bg-muted/20 space-y-4">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                Permission Matrix
              </p>
              <PermissionMatrix grants={role.permissions} />
            </div>

            {role.createdAt && (
              <div className="text-[11px] text-muted-foreground pt-2 border-t">
                <span className="uppercase tracking-wider font-semibold text-[9px] mr-2">Created</span>
                <span className="tabular-nums">{new Date(role.createdAt).toLocaleString()}</span>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

/**
 * A role's own grants, rendered against the SERVED catalogue.
 *
 * B1-T3: the sections and the columns come from
 * `GET /users/permissions/catalogue`, filtered to what this role actually
 * holds. They used to come from a 12-resource `RESOURCE_GROUPS` copy in this
 * file, so a role's `write:tools` — or any grant on the other 40 resources —
 * was silently absent from the matrix that claims to show what it grants.
 *
 * A grant the catalogue does not carry is still SHOWN, in an "Other" section:
 * a key on a role row that this release no longer recognises is exactly the
 * thing an operator needs to see, and dropping it would repeat the defect in a
 * new shape.
 */
/**
 * A display row for the read-only matrix: a catalogue row, widened so the
 * "Other" section can carry a key this release does not recognise.
 *
 * `key`/`action`/`resource` are the precise literal unions on the wire type
 * (they come from the server's own Zod schema, review B1T3-R3) and a key the
 * catalogue has never heard of cannot be typed as one. Widening is confined to
 * this DISPLAY type — nothing here decides authority, and the picker below
 * still works in the exact wire type.
 */
type MatrixRow = Omit<PermissionCatalogueRow, 'key' | 'action' | 'resource'> & {
  key: string;
  action: string;
  resource: string;
};

function PermissionMatrix({ grants }: { grants: Array<{ action: string; resource: string }> }) {
  const {
    catalogue,
    isLoading: catalogueLoading,
    error: catalogueError,
  } = usePermissionCatalogue();

  const held = useMemo(
    () => new Set(grants.map((g) => `${g.action}:${g.resource}`)),
    [grants],
  );

  // The catalogue, narrowed to this role's grants — no reordering, no keys
  // added: the server's order IS the render order.
  const sections = useMemo(() => {
    const kept: MatrixRow[] = catalogue.filter((entry) => held.has(entry.key));
    const groups = groupPermissionCatalogue<MatrixRow>(kept);
    const known = new Set(kept.map((entry) => entry.key));
    const unknown = [...held].filter((key) => !known.has(key)).sort();
    if (unknown.length > 0) {
      groups.push({
        group: 'Other',
        resources: unknown.map((key) => {
          const cut = key.lastIndexOf(':');
          return {
            resource: key.slice(cut + 1),
            entries: [
              {
                key,
                action: key.slice(0, cut),
                resource: key.slice(cut + 1),
                label: key,
                group: 'Other',
                highRisk: false,
                // A key this release does not carry: it is shown, never
                // offered, so the two picker flags are the conservative
                // values. Nothing here decides authority.
                audience: 'tenant' as const,
                nonDelegable: false,
              },
            ],
          };
        }),
      });
    }
    return groups;
  }, [catalogue, held]);

  if (grants.length === 0) {
    return <p className="text-xs text-muted-foreground italic">No permissions granted.</p>;
  }

  // The catalogue has not landed: say WHICH of the two it is (review B1T3-R4).
  // An empty array used to read as "still loading" whatever the reason, so a
  // failed request — a 5xx, a network blip, a 403 because `read:roles` was
  // withdrawn while /users/roles was still cached — showed every expanded role
  // card spinning "Loading permissions…" for ever. Nothing is invented in
  // either branch (principles.md #4).
  if (catalogueError != null) {
    return (
      <p className="text-xs text-red-600">
        Could not load the permission catalogue, so this role&apos;s grants cannot be
        shown. Reload the page to try again.
      </p>
    );
  }
  if (catalogueLoading || catalogue.length === 0) {
    return <p className="text-xs text-muted-foreground italic">Loading permissions…</p>;
  }

  return (
    <div className="space-y-3">
      {sections.map((group) => {
        // Columns: the actions this section actually uses, so the table stays
        // tight — taken from the payload, never from a list in this file.
        const actionsUsed: string[] = [];
        for (const row of group.resources) {
          for (const entry of row.entries) {
            if (!actionsUsed.includes(entry.action)) actionsUsed.push(entry.action);
          }
        }
        return (
          <div key={group.group}>
            <p className="text-[10px] font-semibold text-muted-foreground mb-1.5">{group.group}</p>
            <div className="rounded-md border overflow-x-auto bg-card">
              <table className="text-[10px] min-w-full">
                <thead className="bg-muted/50 text-[9px] uppercase tracking-wider font-semibold text-muted-foreground">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-semibold">Resource</th>
                    {actionsUsed.map((a) => (
                      <th key={a} className="text-center px-2 py-1.5 font-mono whitespace-nowrap">{a}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {group.resources.map((row) => (
                    <tr key={row.resource}>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{row.resource}</td>
                      {actionsUsed.map((a) => (
                        <td key={a} className="px-2 py-1.5 text-center">
                          {held.has(`${a}:${row.resource}`)
                            ? <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 inline" />
                            : <span className="text-muted-foreground/30">·</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Role Editor Dialog ──────────────────────────────────────────────────────

interface RoleEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: RoleRow | null;
}

function RoleEditorDialog({ open, onOpenChange, role }: RoleEditorDialogProps) {
  const isEdit = role !== null;
  const createMutation = useCreateRole();
  const updateMutation = useUpdateRole();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [grants, setGrants] = useState<Set<string>>(new Set());

  // THE catalogue, from the server (B1-T3). Every key it carries gets a
  // checkbox; nothing else does, and no list of keys lives in this file.
  const {
    catalogue,
    isLoading: catalogueLoading,
    error: catalogueError,
  } = usePermissionCatalogue();
  const catalogueGroups = useMemo(
    () => groupPermissionCatalogue(catalogue),
    [catalogue],
  );
  // Columns: the actions the catalogue actually uses, in the order it sends
  // them. Reshaping the payload, not deciding anything about it.
  const actionColumns = useMemo(() => {
    // Typed from the wire schema, not `string` (review B1T3-R3): the same
    // union `highRiskActions` and `byAction` are keyed by, so a rename of the
    // action vocabulary is a compile error here rather than an empty column.
    const out: Array<PermissionCatalogueRow['action']> = [];
    for (const entry of catalogue) {
      if (!out.includes(entry.action)) out.push(entry.action);
    }
    return out;
  }, [catalogue]);
  const highRiskActions = useMemo(
    () => new Set(catalogue.filter((e) => e.highRisk).map((e) => e.action)),
    [catalogue],
  );
  // How many keys a tenant role could actually carry — the denominator the
  // "N of M selected" counter should have used all along (review B1T3-R2).
  // Counting all 156 told an admin they had 156 cells to choose from when ~27
  // of them are refused by the server for every tenant principal there is.
  const grantableCount = useMemo(
    () => catalogue.filter((e) => e.audience === 'tenant' && !e.nonDelegable).length,
    [catalogue],
  );

  // Closes #77 — reset form when dialog opens or role changes.
  //
  // The previous version used `useMemo` for a pure side effect
  // (calling setState during render). When the user opened the
  // dialog → typed name → typed description → selected permissions
  // → clicked "Create role", a parent re-render between any of those
  // steps could trigger React to re-invoke the memo callback in
  // StrictMode / concurrent rendering, snapping the form back to
  // empty state mid-typing. The button stayed enabled because the
  // memo returned the cached value, but the underlying state was
  // re-initialised and the submit lost its data. `useEffect` runs
  // exactly once per (open, role) transition and never during the
  // commit phase that powers the submit handler.
  useEffect(() => {
    if (!open) return;
    setName(role?.name ?? '');
    setDescription(role?.description ?? '');
    const initial = new Set<string>();
    if (role) {
      for (const g of role.permissions) initial.add(`${g.action}:${g.resource}`);
    }
    setGrants(initial);
  }, [open, role]);

  const toggleGrant = (key: string) => {
    setGrants(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Role name is required');
      return;
    }
    if (grants.size === 0) {
      toast.error('Select at least one permission');
      return;
    }

    const permissions = Array.from(grants).map(key => {
      const idx = key.lastIndexOf(':');
      return { action: key.slice(0, idx), resource: key.slice(idx + 1) };
    });

    try {
      if (isEdit && role) {
        await updateMutation.mutateAsync({
          roleId: role.id,
          body: { name, description: description || null, permissions },
        });
        toast.success(`Updated role "${name}"`);
      } else {
        await createMutation.mutateAsync({
          name,
          description: description || null,
          permissions,
        });
        toast.success(`Created role "${name}"`);
      }
      onOpenChange(false);
    } catch (err) {
      const error = err as Error & { response?: { error?: { code?: string; message?: string; details?: unknown } } };
      const code = error.response?.error?.code;
      const message = error.response?.error?.message ?? error.message ?? 'Could not save role';
      if (code === 'PRIVILEGE_ESCALATION') {
        toast.error(`Cannot grant permissions you don't have: ${message}`);
      } else if (code === 'UNGRANTABLE_PERMISSION') {
        // The picker disables these cells, so reaching this means a stale tab
        // or a hand-crafted body. Say what the server said (review BF-2).
        toast.error(`Not grantable through a role: ${message}`);
      } else if (code === 'NAME_CONFLICT') {
        toast.error(`Name conflict: ${message}`);
      } else if (code === 'VALIDATION_ERROR') {
        toast.error(`Validation: ${message}`);
      } else {
        toast.error(message);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit role: ${role?.name}` : 'Create custom role'}</DialogTitle>
          <DialogDescription className="text-xs">
            Custom roles can grant only permissions you currently have. The server enforces this guard.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto pr-1">
          <div className="space-y-2">
            <Label className="text-xs">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Knowledge Editor"
              maxLength={100}
              className="text-sm"
              disabled={isPending}
            />
            <p className="text-[10px] text-muted-foreground">
              Letters, numbers, spaces, hyphens, underscores. Cannot match a system role name.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Description (optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this role is for and who should hold it…"
              maxLength={500}
              rows={2}
              className="text-sm"
              disabled={isPending}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Permissions</Label>
              <span className="text-[10px] text-muted-foreground tabular-nums">
                {grants.size} of {grantableCount} selected
              </span>
            </div>
            {/*
              Permission matrix (closes #137): rows are resources grouped by
              rail, columns are actions, the resource column and the header
              are sticky.

              B1-T3 — every row, column, group and risk glyph comes from
              `GET /users/permissions/catalogue`. It used to come from four
              constants at the top of this file that covered 12 of the
              platform's 52 resources, so the keys on the other 40 could not
              be granted here at all.
            */}
            <ScrollArea className="h-[40vh] rounded-md border">
              <div className="overflow-x-auto">
                {catalogueError ? (
                  <p className="p-4 text-xs text-muted-foreground">
                    {/*
                      No invented fallback. A plausible default list here is
                      exactly how the mirrored copy came to exist, and it would
                      let an admin build a role out of keys the server does not
                      recognise (principles.md #4).
                    */}
                    Could not load the permission catalogue. Close and reopen
                    this dialog to try again.
                  </p>
                ) : catalogueLoading ? (
                  <p className="p-4 text-xs text-muted-foreground italic">
                    Loading permissions…
                  </p>
                ) : (
                  <table className="w-full text-xs border-collapse">
                    <thead className="sticky top-0 z-10 bg-card">
                      <tr className="border-b">
                        <th className="sticky left-0 bg-card text-[10px] font-semibold uppercase tracking-wider text-muted-foreground text-left px-3 py-2 min-w-[160px] border-r">
                          Resource
                        </th>
                        {actionColumns.map((action) => (
                          <th
                            key={action}
                            className="text-[10px] font-mono font-semibold text-muted-foreground text-center px-2 py-2 whitespace-nowrap"
                            title={
                              highRiskActions.has(action)
                                ? `${action} — high-risk action`
                                : action
                            }
                          >
                            {action}
                            {highRiskActions.has(action) && (
                              <span
                                className="ml-1 text-amber-500"
                                aria-label="high-risk action"
                              >
                                ⚠
                              </span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {catalogueGroups.map((group) => (
                        <Fragment key={group.group}>
                          <tr>
                            <td
                              colSpan={actionColumns.length + 1}
                              className="sticky left-0 bg-muted/30 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                            >
                              {group.group}
                            </td>
                          </tr>
                          {group.resources.map((row) => {
                            const byAction = new Map(
                              row.entries.map((entry) => [entry.action, entry]),
                            );
                            const rowGrantCount = row.entries.reduce(
                              (sum, entry) => sum + (grants.has(entry.key) ? 1 : 0),
                              0,
                            );
                            return (
                              <tr
                                key={row.resource}
                                className="border-b last:border-b-0 hover:bg-muted/30"
                              >
                                <td className="sticky left-0 bg-card group-hover:bg-muted/30 px-3 py-1.5 border-r">
                                  <code className="text-[11px] font-mono">{row.resource}</code>
                                  {rowGrantCount > 0 && (
                                    <span className="ml-2 text-[9px] text-muted-foreground tabular-nums">
                                      {rowGrantCount}/{row.entries.length}
                                    </span>
                                  )}
                                </td>
                                {actionColumns.map((action) => {
                                  const entry = byAction.get(action);
                                  // A cell with no entry is not a permission
                                  // this release has: there is nothing to grant
                                  // and no checkbox to draw. The old grid drew
                                  // one in every cell of a 13x12 product, most
                                  // of which were not keys at all.
                                  if (!entry) {
                                    return (
                                      <td
                                        key={action}
                                        className="text-center px-2 py-1.5 text-muted-foreground/25"
                                        aria-hidden="true"
                                      >
                                        ·
                                      </td>
                                    );
                                  }
                                  const checked = grants.has(entry.key);
                                  // A key a tenant role can never carry
                                  // (reviews B1T3-R2 / BF-2). `nonDelegable`
                                  // is answered by role key, never by grant:
                                  // the Owner holds `factory:reset:tenants`,
                                  // so the cell is shown — an operator needs
                                  // to see the key exists and that it is
                                  // reserved — rather than offering a
                                  // checkbox that guarantees a 422
                                  // UNGRANTABLE_PERMISSION on save.
                                  //
                                  // The `audience === 'platform'` arm is now
                                  // a FAIL-CLOSED guard, not the normal path
                                  // (review SEC-B1b-06): the route serves
                                  // `TENANT_PERMISSION_CATALOGUE`, so the
                                  // sysadmin slate no longer reaches this
                                  // browser at all. If a platform key ever
                                  // arrives again it is drawn disabled — the
                                  // picker never offers one on the strength
                                  // of it having been sent.
                                  const refusedReason = entry.nonDelegable
                                    ? 'Reserved to the shipped Owner role — no other role can grant it'
                                    : entry.audience === 'platform'
                                      ? 'Platform administrator permission — not held by any tenant role'
                                      : null;
                                  if (refusedReason) {
                                    return (
                                      <td
                                        key={action}
                                        className="text-center px-2 py-1.5"
                                        title={`${entry.label} — ${refusedReason}`}
                                      >
                                        <Checkbox
                                          checked={false}
                                          disabled
                                          aria-label={`${entry.key} — ${refusedReason}`}
                                        />
                                      </td>
                                    );
                                  }
                                  return (
                                    <td
                                      key={action}
                                      className="text-center px-2 py-1.5"
                                      title={
                                        entry.highRisk && checked
                                          ? `${entry.label} — high-risk grant`
                                          : entry.label
                                      }
                                    >
                                      <Checkbox
                                        checked={checked}
                                        onCheckedChange={() => toggleGrant(entry.key)}
                                        disabled={isPending}
                                        aria-label={entry.key}
                                      />
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </ScrollArea>
            {grants.size === 0 && (
              <Alert className="border-amber-500/40 bg-amber-500/5">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertDescription className="text-[11px] text-amber-800 dark:text-amber-300">
                  Select at least one permission. A role with no permissions cannot be saved.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isPending || !name.trim() || grants.size === 0}
          >
            {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Delete Role Dialog ──────────────────────────────────────────────────────

function DeleteRoleDialog({
  role, onClose,
}: {
  role: RoleRow | null;
  onClose: () => void;
}) {
  const deleteMutation = useDeleteRole();

  // WHAT THE SERVER WILL DO, from the fact the server decides from: has this
  // role ever been assigned (`countAssignmentRowsEverForRole` — every row on
  // file, expired rows and rows belonging to deleted users included).
  //
  // It used to be `assignedUserCount > 0` — the LIVE holder count, which
  // filters exactly those rows out — so for a role whose only holder had since
  // been deleted the dialog promised "Nobody holds it, so nothing is left
  // behind. This cannot be undone", the server retired the row, and the toast
  // said "Retired role": the screen told the operator the opposite of what
  // happened (review B1-T4-R2 / B1T4-04).
  const willRetire = role?.everAssigned === true;

  const handleDelete = async () => {
    if (!role) return;
    try {
      const result = await deleteMutation.mutateAsync({ roleId: role.id });
      toast.success(
        result.retired ? `Retired role "${role.name}"` : `Deleted role "${role.name}"`,
        result.retired
          ? {
              description:
                'Everyone who held it stops holding it. The assignment history is kept.',
            }
          : undefined,
      );
      onClose();
    } catch (err) {
      const error = err as Error & { response?: { error?: { code?: string; message?: string } } };
      toast.error(error.response?.error?.message ?? error.message ?? 'Could not remove role');
    }
  };

  return (
    <Dialog open={role !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            {willRetire ? 'Retire role' : 'Delete role'}
          </DialogTitle>
          {/*
            A role that has ever been assigned is RETIRED, not deleted, so its
            history survives (upgrade-model.md §3) — and the screen says which
            of the two will happen BEFORE the click, instead of refusing with a
            count of assignments it cannot show.
          */}
          <DialogDescription className="text-xs">
            {willRetire ? (
              (role?.assignedUserCount ?? 0) > 0 ? (
                <>
                  Retire the role <span className="font-semibold">{role?.name}</span>. The{' '}
                  {role?.assignedUserCount} people who hold it stop holding it immediately;
                  the record of who held it is kept.
                </>
              ) : (
                <>
                  Retire the role <span className="font-semibold">{role?.name}</span>. Nobody
                  holds it now, but it has been assigned before — so the row is kept and
                  marked retired, rather than deleted, and the record of who held it
                  survives.
                </>
              )
            ) : (
              <>
                Delete the role <span className="font-semibold">{role?.name}</span>. Nobody
                holds it, so nothing is left behind. This cannot be undone.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending
              ? willRetire ? 'Retiring…' : 'Deleting…'
              : willRetire ? 'Retire role' : 'Delete role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
